import { useState } from "react";
import { Loader2, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const fmtWhen = (iso) => {
  try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

// Choose which SbN sessions to pull; same-day sessions arrive pre-selected.
export function SbnSessionPicker({ sessions, suggestedIds, onPull, onCancel }) {
  const [selected, setSelected] = useState(() => new Set(suggestedIds || []));
  const [busy, setBusy] = useState(false);
  const toggle = (sid) => setSelected((cur) => {
    const next = new Set(cur);
    next.has(sid) ? next.delete(sid) : next.add(sid);
    return next;
  });

  async function pull() {
    setBusy(true);
    try { await onPull(Array.from(selected)); }
    catch (e) { toast.error("Couldn't pull SbN results", { description: e?.response?.data?.detail || e.message }); }
    finally { setBusy(false); }
  }

  if (!sessions?.length) {
    return <p className="text-sm text-zinc-500" data-testid="sbn-no-sessions">No SbN sessions recorded for this client yet.</p>;
  }
  return (
    <div className="space-y-2" data-testid="sbn-session-picker">
      <p className="text-xs text-zinc-500">Select the SbN session(s) from this visit — same-day sessions are pre-selected.</p>
      <ul className="divide-y rounded-md border border-zinc-200 max-h-64 overflow-y-auto">
        {sessions.map((s) => {
          const empty = !s.tests.length;
          return (
            <li key={s.session_id} className={`flex items-start gap-3 px-3 py-2 ${empty ? "opacity-50" : ""}`}>
              <Checkbox
                checked={selected.has(s.session_id)}
                onCheckedChange={() => toggle(s.session_id)}
                disabled={empty}
                className="mt-0.5"
                data-testid={`sbn-session-check-${s.session_id}`}
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-zinc-900">{fmtWhen(s.time)}</div>
                <div className="text-xs text-zinc-500 truncate">
                  {empty ? "No results recorded" : s.tests.map((t) => t.test).join(" · ")}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex gap-2">
        <Button size="sm" onClick={pull} disabled={busy || selected.size === 0} data-testid="sbn-pull-btn">
          {busy ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
          Pull into assessment
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel} data-testid="sbn-pull-cancel-btn">Cancel</Button>
        )}
      </div>
    </div>
  );
}
